// @ts-check
import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { Skeleton, TooltipPosition, Flex, FlexItem } from '@patternfly/react-core';
import _ from 'lodash';

import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import { useAlarmPushService } from 'services/AlarmApi/AlarmApi';
import { NodeType } from 'utils/types';
import useAPI from 'services/useAPI';
import AlarmIconMulti from 'components/Alarm/Icons/AlarmIconMulti';
import { createPathUrl } from 'components/NodePage/location';
import createQueryRequest from 'services/createQueryRequest';
// import { connectionIcon } from 'utils/hooks/useConnectionStatus';
import { connectionIcon, useConnectionStatus } from 'utils/hooks/useConnectionStatus';

import styles from './AssetList.module.css';
import { getProviderClientList } from 'utils/asset';
import { isoToRelative } from 'utils/time';
import { assetKeys } from 'components/Admin/Assets/queries';

/**
 * @typedef {import('utils/types').AssetType} AssetType
 */


/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<AssetType>}
 * @constant
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

/**
 * @typedef {AssetType & { connectionStatus: import('utils/hooks/useConnectionStatus').ConnectionStatus[] } & { alarms: any[] }} AssetWithInfo
 */

// Very arbitrary
const invalidLastActivityTime = DateTime.fromObject({ year: 1600, month: 1, day: 1 });

/**
 * @param {AssetType} asset
 * @returns {AssetType & { lastActivityTime : DateTime | null }}
 */
const formatAsset = (asset) => {
  const deviceLastActivity = DateTime.fromISO(asset.ioTDevice?.lastActivityTime);

  const lastActivityTime = DateTime.max(deviceLastActivity);
  const isValidLastActivityTime = lastActivityTime > invalidLastActivityTime;

  return {
    ...asset,
    lastActivityTime: isValidLastActivityTime ? DateTime.fromISO(lastActivityTime.toString()) : null
  };
};

/**
 * TODO: Does the asset already contain alarms? Also add type to alarmList
 * @param {(AssetType & { alarms: any[] })[]} assetList
 * @param {object[]} alarmList
 * @returns {(AssetType & { alarms: any[] })[]}
 */
const mergeAlarmsIntoAssets = (assetList, alarmList) => {
  const updatedAssets = [];

  for (let asset of assetList) {
    const latestAlarms = _.filter(alarmList, alarm => alarm.entityId === asset.id);
    const alarms = !_.isEmpty(latestAlarms) ? latestAlarms : asset.alarms ?? [];
    const updatedAsset = {
      ...formatAsset(asset),
      alarms
    };

    updatedAssets.push(updatedAsset);
  }

  return updatedAssets;
};

/**
 *
 * @param {AssetType[]} assetList
 * @param {import('utils/hooks/useConnectionStatus').ConnectionStatus[]} connectionStatusList
 * @returns {(AssetType & { connectionStatus: import('utils/hooks/useConnectionStatus').ConnectionStatus[] } )[]}
 */
const mergeConnectionStatus = (assetList, connectionStatusList) => {
  const updateAssets = _.map(assetList, (asset) => {
    const providerClients = getProviderClientList([asset]);
    const list = _.map(providerClients, (item) => {
      const filteredConnectionStatusList = _.filter(connectionStatusList, status => status.providerClientId === item.providerClientId);
      return filteredConnectionStatusList;
    });
    return { ...asset, connectionStatus: _.flatten(list) };
  });
  return updateAssets;
};

const AssetList = ({ assetIds, baseUrl }) => {
  const T = useTranslation();
  const api = useAPI();

  const assetsQuery = useQuery(assetKeys.asset(), createQueryRequest(api.core.getAssets));
  const isLoadingAssets = assetsQuery.isLoading;
  const [assets, visibleAssetIds] = useMemo(() => {
    const ids = _.map(_.filter(assetIds, (item) => (item.type === NodeType.Asset)), 'id');
    const assets = _.filter(assetIds, (item) => (item.type === NodeType.Asset));
    const assetInstances = _.filter(/** @type {AssetType[]}*/(assetsQuery.data), (asset) => _.includes(ids, asset.id));
    const mergedAssets = _.map(assetInstances, (asset) => _.assign(asset, _.filter(assets, a => asset.id === a.id)[0]));
    return [mergedAssets, _.map(mergedAssets, 'id')];
  }, [assetIds, assetsQuery.data]);
  const [perPage, setPerPage] = React.useState(20);
  const [page, setPage] = React.useState(1);
  const [startIdx, setStartIdx] = React.useState(0);
  const [endIdx, setEndIdx] = React.useState(20);


  const [alarms, isLoadingAlarms] = useAlarmPushService(useMemo(() => ({ assetIds: visibleAssetIds }),
    [visibleAssetIds]));


  const { connectionStatusList } = useConnectionStatus(_.slice(assets, startIdx, endIdx));

  const mergedAssets = /** @type {AssetWithInfo[]} */(useMemo(() => {
    // const connectionStatusList = [];
    if (!_.isEmpty(assets)) {
      return mergeConnectionStatus(mergeAlarmsIntoAssets(assets, alarms), connectionStatusList);
      // return mergeAlarmsIntoAssets(assets, alarms);
    }
    return [];
  }, [assets, alarms, connectionStatusList]));

  const visibleAssets = useMemo(() => {
    return _.slice(mergedAssets, startIdx, endIdx);
  }, [mergedAssets, startIdx, endIdx]);

  // eslint-disable-next-line no-unused-vars
  const onSetPage = useCallback((event, newPage, perPage, startIdx, endIdx) => {
    setPage(newPage);
    setStartIdx(startIdx);
    setEndIdx(endIdx);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const onPerPageSelect = useCallback((event, newPerPage, newPage, startIdx, endIdx) => {
    setPerPage(newPerPage);
    setPage(newPage);
    setStartIdx(startIdx);
    setEndIdx(endIdx);
  }, []);

  const pageInfo = useMemo(() => ({
    page,
    perPage,
    itemCount: mergedAssets.length,
    onSetPage,
    onPerPageSelect
  }), [mergedAssets.length, onPerPageSelect, onSetPage, page, perPage]);

  /**
   * @type {import('components/Common/SimpleTable/SimpleTable').ColumnType<AssetWithInfo>[]}
   */
  const columns = useMemo(() => {
    return [
      {
        label: T.common.name,
        path: '.',
        render: asset => <Link to={createPathUrl(asset, baseUrl)} title={asset.displayName}>{ asset.displayName }</Link>
      },
      {
        label: T.common.alarms,
        path: 'alarms',
        render: alarms => <AlarmIconMulti data={alarms} tooltipDirection={TooltipPosition.right} />

      },
      {
        label: T.common.lastActive,
        path: 'connectionStatus',
        render: status => {
          return isoToRelative(_.first(status)?.lastActive);
        }
      },
      {
        label: T.asset.connectionStatus,
        path: 'connectionStatus',
        render: (connectionStatus, row) => {
          const items = (_.map(connectionStatus, (item, index) => {
            return <FlexItem key={row.id + '_' + row.modelId + '_connectionStatus_' + index}>{ connectionIcon(item) }</FlexItem>;
          }));

          return (<Flex>
            { items }
          </Flex>);
          // // consle.log('connectionStatus', connectionStatus);
          // return <Icons.Connected />;
        }
      }
    ];
  }, [T.asset.connectionStatus, T.common.alarms, T.common.lastActive, T.common.name, baseUrl]);


  // TODO: Show error

  if (mergedAssets == null || isLoadingAssets || isLoadingAlarms) {
    return <div className={styles.skeletonWrapper}><Skeleton height="100%" /></div>;
  }


  return (
    <SimpleTable
      pageInfo={pageInfo}
      isLoading={isLoadingAlarms || isLoadingAssets}
      data={visibleAssets}
      columns={columns}
      sortBy={DefaultSortBy}
      compact
      // usePaging
      // pageSize={25}
    />
  );
};

export default AssetList;
